import React, { useState } from 'react';
import TemplateManager from '../components/TemplateManager';
import SearchableSelect from '../components/SearchableSelect';
import JsonViewer from '../components/JsonViewer';
import ApiDocumentation from '../components/ApiDocumentation';
import { STATES } from '../utils/constants';
import { calculatePremium } from '../services/api';
import JSZip from 'jszip';

const ArtisanEvolve = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [showDocumentation, setShowDocumentation] = useState(false);

  // Безопасная функция для отображения значений
  const safeDisplay = (value, fallback = 'N/A') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Получаем даты по умолчанию
  const today = new Date();
  const nextYear = new Date(today);
  nextYear.setFullYear(nextYear.getFullYear() + 1);

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Функция для расчета expiration date (+1 год от effective date)
  const calculateExpirationDate = (effectiveDate) => {
    const date = new Date(effectiveDate);
    date.setFullYear(date.getFullYear() + 1);
    return formatDate(date);
  };

  // Обработчик для effective date с автоматическим расчетом expiration date
  const handleEffectiveDateChange = (effectiveDate) => {
    const expirationDate = calculateExpirationDate(effectiveDate);

    setFormData({
      ...formData,
      policy: {
        ...formData.policy,
        effectiveDate,
        expirationDate
      }
    });
  };

  const [versionNumber, setVersionNumber] = useState('1.0.0');

  const [formData, setFormData] = useState({
    account: {
      location: {
        id1138698: {
          county: 'DUVAL',
          state: 'FL',
          extraData: {
            idOriginal: 1138698
          }
        }
      }
    },
    policy: {
      state: 'FL',
      rateType: 'NB',
      effectiveDate: formatDate(today),
      expirationDate: formatDate(nextYear),
      company: 'Evolve',
      companyShort: 'Evolve',
      lineGL: {
        risk: {
          '91341_1': {
            code: 91341,
            exposure: 150000,
            extraData: {
              locationId: 1138698
            }
          }
        },
        policyPerOccurrenceLimit: 1000000,
        policyAggregateLimit: 2000000,
        productCompletedOperationLimit: 2000000,
        personalAdvertisingInjuryLimit: 1000000,
        deductibles: {
          pd: 500.00
        },
        optionalCoverages: {
          additionalInsured: {},
          Miscellaneous: {}
        }
      },
      lossHistory: {
        items: {},
        priorClaim3years: 'no'
      },
      previousInsuranceItems: {},
      optionalCoverages: {
        miscellaneous: {
          terrorism: {
            name: 'TRIA',
            title: 'Terrorism Risk Insurance Act Coverage'
          }
        }
      },
      specialCrDr: {
        largeRiskDiscountFactor: '-select-',
        management: '0',
        safetyPrograms: '0',
        supervisionEmployeeExperience: '0',
        specialCharacteristics: '0'
      },
      lineIM: {
        coverages: {}
      }
    }
  });

  // Функции для работы с локациями
  const getLocationsList = () => {
    return Object.keys(formData.account.location).map(locationId => ({
      id: locationId,
      ...formData.account.location[locationId]
    }));
  };

  const getRisksList = () => {
    return Object.keys(formData.policy.lineGL.risk).map(riskKey => ({
      key: riskKey,
      ...formData.policy.lineGL.risk[riskKey]
    }));
  };

  // Опции для формы - валидные комбинации лимитов
  const GL_LIMIT_COMBINATIONS = [
    {
      perOccurrence: 100000,
      aggregate: 200000,
      label: '$100,000 / $200,000',
      perOccurrenceLabel: '$100,000',
      aggregateLabel: '$200,000'
    },
    {
      perOccurrence: 300000,
      aggregate: 600000,
      label: '$300,000 / $600,000',
      perOccurrenceLabel: '$300,000',
      aggregateLabel: '$600,000'
    },
    {
      perOccurrence: 500000,
      aggregate: 1000000,
      label: '$500,000 / $1,000,000',
      perOccurrenceLabel: '$500,000',
      aggregateLabel: '$1,000,000'
    },
    {
      perOccurrence: 1000000,
      aggregate: 2000000,
      label: '$1,000,000 / $2,000,000',
      perOccurrenceLabel: '$1,000,000',
      aggregateLabel: '$2,000,000'
    }
  ];

  const GL_LIMITS = [
    { value: 100000, label: '$100,000' },
    { value: 300000, label: '$300,000' },
    { value: 500000, label: '$500,000' },
    { value: 1000000, label: '$1,000,000' },
    { value: 2000000, label: '$2,000,000' }
  ];

  const ARTISAN_STATES = [
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'LA', label: 'Louisiana' }
  ];

  const DEDUCTIBLES = [
    { value: 0, label: '$0' },
    { value: 250, label: '$250' },
    { value: 500, label: '$500' },
    { value: 1000, label: '$1,000' },
    { value: 2000, label: '$2,000' },
    { value: 5000, label: '$5,000' }
  ];

  const CLASS_CODES = [
    // HVAC & Air Conditioning
    { value: 13590, label: "13590 - HVAC Systems - NOC" },
    { value: 14913, label: "14913 - HVAC Installation & Service" },
    { value: 91111, label: "91111 - Air Conditioning Systems" },

    // Appliance Installation
    { value: 91150, label: "91150 - Appliance Installation - Commercial" },
    { value: 91155, label: "91155 - Appliance Installation - Household/Residential" },

    // General Construction
    { value: 91315, label: "91315 - Construction - General" },
    { value: 91340, label: "91340 - Carpentry - Residential Construction (3 stories or less)" },
    { value: 91341, label: "91341 - Electrical Work - Within Buildings" },
    { value: 91342, label: "91342 - Plumbing" },
    { value: 91343, label: "91343 - Heating Systems" },
    { value: 91344, label: "91344 - Carpentry - Handyman" },

    // Specialty Construction Services
    { value: 91405, label: "91405 - Carpet, Rug, Furniture Cleaning" },
    { value: 91436, label: "91436 - Ceiling/Wall Installation - Metal" },
    { value: 91551, label: "91551 - Communication Equipment Installation" },
    { value: 91560, label: "91560 - Concrete Construction" },
    { value: 91581, label: "91581 - Carpentry - Interior" },
    { value: 91583, label: "91583 - Contractors - Subcontracted Work" },
    { value: 91584, label: "91584 - Construction Specialties" },
    { value: 91585, label: "91585 - Construction Services - NOC" },
    { value: 91629, label: "91629 - Debris Removal - Construction Site" },
    { value: 91746, label: "91746 - Door/Window Installation - Metal" },

    // Drywall & Electrical
    { value: 92215, label: "92215 - Driveway/Sidewalk Paving" },
    { value: 92338, label: "92338 - Drywall" },
    { value: 92451, label: "92451 - Electrical Apparatus" },
    { value: 92478, label: "92478 - Electrical Work" },

    // Fencing & Fixtures
    { value: 94276, label: "94276 - Fence Erection" },
    { value: 94569, label: "94569 - Tile, Stone, Marble Work" },
    { value: 95124, label: "95124 - Furniture/Fixtures Installation" },
    { value: 95233, label: "95233 - General Maintenance Services" },
    { value: 95410, label: "95410 - Grading of Land" },
    { value: 95625, label: "95625 - Painting - Interior" },

    // House Services & Installation
    { value: 96053, label: "96053 - House Furnishing Installation" },
    { value: 96408, label: "96408 - Home Improvement Services" },
    { value: 96410, label: "96410 - Insulation Work - Mineral" },
    { value: 96611, label: "96611 - Installation Services - NOC" },
    { value: 96816, label: "96816 - Janitorial - Commercial" },

    // Landscaping & Lawn Care
    { value: 97047, label: "97047 - Landscaping Gardening" },
    { value: 97050, label: "97050 - Lawn Care Services" },
    { value: 97111, label: "97111 - Landscape Installation" },
    { value: 97220, label: "97220 - Landscaping" },
    { value: 97447, label: "97447 - Fence Installation" },

    // Painting & Plastering
    { value: 98111, label: "98111 - Painting - Exterior (Commercial)" },
    { value: 98304, label: "98304 - Painting - Exterior (3 stories or less)" },
    { value: 98305, label: "98305 - Painting - Interior" },
    { value: 98344, label: "98344 - Paperhanging" },
    { value: 98449, label: "98449 - Plastering/Stucco Work" },
    { value: 98502, label: "98502 - Prefabricated Building Erection" },
    { value: 98805, label: "98805 - Septic Tank System" },
    { value: 98967, label: "98967 - Service & Repair - NOC" },
    { value: 98993, label: "98993 - Specialty Trade Services" },

    // Specialty Services
    { value: 99003, label: "99003 - Sign Painting/Lettering - Interior" },
    { value: 99004, label: "99004 - Sign Painting/Lettering - Buildings" },
    { value: 99080, label: "99080 - Small Engine Repair" },
    { value: 99505, label: "99505 - Swimming Pool Servicing" },
    { value: 99507, label: "99507 - Swimming Pool Construction" },
    { value: 99650, label: "99650 - Television/Radio/Satellite Installation" },
    { value: 99709, label: "99709 - Tree Service - Ground Level" },
    { value: 99746, label: "99746 - Tile, Stone, Marble Work - Interior" },
    { value: 99777, label: "99777 - Utility Services" },
    { value: 99827, label: "99827 - Upholstering Services" },
    { value: 99952, label: "99952 - Water Well Drilling" },
    { value: 99955, label: "99955 - Weatherproofing" },
    { value: 99975, label: "99975 - Window Cleaning" }
  ];

  const AI_FORMS = [
    { value: 'AUGL1001', label: 'AUGL1001 - Omnibus Additional Insured Form ($190)' },
    { value: 'AUGL1002', label: 'AUGL1002 - Automatic Waiver of Subrogation for Additional Insureds ($150)' },
    { value: 'AUGL1003', label: 'AUGL1003 - Automatic Additional Insured - 20 37 Form ($500)' },
    { value: 'AUGL1004', label: 'AUGL1004 - Automatic Additional Insured - 20 10 Form ($100)' },
    { value: 'AUGL993', label: 'AUGL993 - Additional Insured (Primary and Noncontributory) ($150)' },
    { value: 'CG2005', label: 'CG2005 - Additional Insured - Controlling Interest ($50)' },
    { value: 'CG2007', label: 'CG2007 - Additional Insured - Engineers, Architects or Surveyors ($50)' },
    { value: 'CG2010', label: 'CG2010 - Additional Insured - Owners, Lessees or Contractors ($150)' },
    { value: 'CG2011', label: 'CG2011 - Additional Insured - Managers/Lessors of Premises ($50)' },
    { value: 'CG2012', label: 'CG2012 - Additional Insured - State or Political Subdivisions ($50)' },
    { value: 'CG2018', label: 'CG2018 - Additional Insured - Mortgagee/Assignee/Receiver ($50)' },
    { value: 'CG2024', label: 'CG2024 - Additional Insured - Owners from Whom Land has been Leased ($50)' },
    { value: 'CG2026', label: 'CG2026 - Additional Insured - Designated Person or Organization ($50)' },
    { value: 'CG2028', label: 'CG2028 - Additional Insured - Lessor of Leased Equipment ($50)' },
    { value: 'CG2029', label: 'CG2029 - Additional Insured - Grantor of Franchise ($50)' },
    { value: 'CG2037', label: 'CG2037 - Additional Insured - Completed Operations ($350)' },
    { value: 'CG2404', label: 'CG2404 - CG2404 ($50)' },
    { value: 'CG2503', label: 'CG2503 - CG2503 ($100)' },
    { value: 'CG2504', label: 'CG2504 - CG2504 ($100)' }
  ];

  const COUNTIES_BY_STATE = {
    FL: [
      { value: 'MIAMI-DADE', label: 'Miami-Dade' },
      { value: 'BROWARD', label: 'Broward' },
      { value: 'MONROE', label: 'Monroe' },
      { value: 'PALM BEACH', label: 'Palm Beach' },
      { value: 'HILLSBOROUGH', label: 'Hillsborough' },
      { value: 'PINELLAS', label: 'Pinellas' },
      { value: 'DUVAL', label: 'Duval' },
      { value: 'ALACHUA', label: 'Alachua' },
      { value: 'BAKER', label: 'Baker' },
      { value: 'BAY', label: 'Bay' },
      { value: 'BRADFORD', label: 'Bradford' },
      { value: 'BREVARD', label: 'Brevard' },
      { value: 'CALHOUN', label: 'Calhoun' },
      { value: 'CHARLOTTE', label: 'Charlotte' },
      { value: 'CITRUS', label: 'Citrus' },
      { value: 'CLAY', label: 'Clay' },
      { value: 'COLLIER', label: 'Collier' },
      { value: 'COLUMBIA', label: 'Columbia' },
      { value: 'DE SOTO', label: 'De Soto' },
      { value: 'DIXIE', label: 'Dixie' },
      { value: 'ESCAMBIA', label: 'Escambia' },
      { value: 'FLAGLER', label: 'Flagler' },
      { value: 'FRANKLIN', label: 'Franklin' },
      { value: 'GADSDEN', label: 'Gadsden' },
      { value: 'GILCHRIST', label: 'Gilchrist' },
      { value: 'GLADES', label: 'Glades' },
      { value: 'GULF', label: 'Gulf' },
      { value: 'HAMILTON', label: 'Hamilton' },
      { value: 'HARDEE', label: 'Hardee' },
      { value: 'HENDRY', label: 'Hendry' },
      { value: 'HERNANDO', label: 'Hernando' },
      { value: 'HIGHLANDS', label: 'Highlands' },
      { value: 'HOLMES', label: 'Holmes' },
      { value: 'INDIAN RIVER', label: 'Indian River' },
      { value: 'JACKSON', label: 'Jackson' },
      { value: 'JEFFERSON', label: 'Jefferson' },
      { value: 'LAFAYETTE', label: 'Lafayette' },
      { value: 'LAKE', label: 'Lake' },
      { value: 'LEE', label: 'Lee' },
      { value: 'LEON', label: 'Leon' },
      { value: 'LEVY', label: 'Levy' },
      { value: 'LIBERTY', label: 'Liberty' },
      { value: 'MADISON', label: 'Madison' },
      { value: 'MANATEE', label: 'Manatee' },
      { value: 'MARION', label: 'Marion' },
      { value: 'MARTIN', label: 'Martin' },
      { value: 'NASSAU', label: 'Nassau' },
      { value: 'OKALOOSA', label: 'Okaloosa' },
      { value: 'OKEECHOBEE', label: 'Okeechobee' },
      { value: 'ORANGE', label: 'Orange' },
      { value: 'OSCEOLA', label: 'Osceola' },
      { value: 'PASCO', label: 'Pasco' },
      { value: 'POLK', label: 'Polk' },
      { value: 'PUTNAM', label: 'Putnam' },
      { value: 'SAINT JOHNS', label: 'Saint Johns' },
      { value: 'SAINT LUCIE', label: 'Saint Lucie' },
      { value: 'SANTA ROSA', label: 'Santa Rosa' },
      { value: 'SARASOTA', label: 'Sarasota' },
      { value: 'SEMINOLE', label: 'Seminole' },
      { value: 'SUMTER', label: 'Sumter' },
      { value: 'SUWANNEE', label: 'Suwannee' },
      { value: 'TAYLOR', label: 'Taylor' },
      { value: 'UNION', label: 'Union' },
      { value: 'VOLUSIA', label: 'Volusia' },
      { value: 'WAKULLA', label: 'Wakulla' },
      { value: 'WALTON', label: 'Walton' },
      { value: 'WASHINGTON', label: 'Washington' }
    ],
    GA: [
      { value: 'APPLING', label: 'Appling' },
      { value: 'ATKINSON', label: 'Atkinson' },
      { value: 'BACON', label: 'Bacon' },
      { value: 'BALDWIN', label: 'Baldwin' },
      { value: 'BANKS', label: 'Banks' },
      { value: 'BARROW', label: 'Barrow' },
      { value: 'BARTOW', label: 'Bartow' },
      { value: 'BEN HILL', label: 'Ben Hill' },
      { value: 'BERRIEN', label: 'Berrien' },
      { value: 'BIBB', label: 'Bibb' },
      { value: 'BLECKLEY', label: 'Bleckley' },
      { value: 'BRANTLEY', label: 'Brantley' },
      { value: 'BROOKS', label: 'Brooks' },
      { value: 'BRYAN', label: 'Bryan' },
      { value: 'BULLOCH', label: 'Bulloch' },
      { value: 'BURKE', label: 'Burke' },
      { value: 'BUTTS', label: 'Butts' },
      { value: 'CALHOUN', label: 'Calhoun' },
      { value: 'CAMDEN', label: 'Camden' },
      { value: 'CANDLER', label: 'Candler' },
      { value: 'CARROLL', label: 'Carroll' },
      { value: 'CATOOSA', label: 'Catoosa' },
      { value: 'CHARLTON', label: 'Charlton' },
      { value: 'CHATHAM', label: 'Chatham' },
      { value: 'CHATTAHOOCHEE', label: 'Chattahoochee' },
      { value: 'CHATTOOGA', label: 'Chattooga' },
      { value: 'CHEROKEE', label: 'Cherokee' },
      { value: 'CITY OF ATLANTA', label: 'City of Atlanta' },
      { value: 'CLARKE', label: 'Clarke' },
      { value: 'CLAY', label: 'Clay' },
      { value: 'CLAYTON', label: 'Clayton' },
      { value: 'CLINCH', label: 'Clinch' },
      { value: 'COBB', label: 'Cobb' },
      { value: 'COFFEE', label: 'Coffee' },
      { value: 'COLQUITT', label: 'Colquitt' },
      { value: 'COLUMBIA', label: 'Columbia' },
      { value: 'COOK', label: 'Cook' },
      { value: 'COWETA', label: 'Coweta' },
      { value: 'CRAWFORD', label: 'Crawford' },
      { value: 'CRISP', label: 'Crisp' },
      { value: 'DADE', label: 'Dade' },
      { value: 'DAWSON', label: 'Dawson' },
      { value: 'DECATUR', label: 'Decatur' },
      { value: 'DEKALB', label: 'DeKalb' },
      { value: 'DODGE', label: 'Dodge' },
      { value: 'DOOLY', label: 'Dooly' },
      { value: 'DOUGHERTY', label: 'Dougherty' },
      { value: 'DOUGLAS', label: 'Douglas' },
      { value: 'EARLY', label: 'Early' },
      { value: 'ECHOLS', label: 'Echols' },
      { value: 'EFFINGHAM', label: 'Effingham' },
      { value: 'ELBERT', label: 'Elbert' },
      { value: 'EMANUEL', label: 'Emanuel' },
      { value: 'EVANS', label: 'Evans' },
      { value: 'FANNIN', label: 'Fannin' },
      { value: 'FAYETTE', label: 'Fayette' },
      { value: 'FLOYD', label: 'Floyd' },
      { value: 'FORSYTH', label: 'Forsyth' },
      { value: 'FRANKLIN', label: 'Franklin' },
      { value: 'FULTON', label: 'Fulton' },
      { value: 'GILMER', label: 'Gilmer' },
      { value: 'GLASCOCK', label: 'Glascock' },
      { value: 'GLYNN', label: 'Glynn' },
      { value: 'GORDON', label: 'Gordon' },
      { value: 'GRADY', label: 'Grady' },
      { value: 'GREENE', label: 'Greene' },
      { value: 'GWINNETT', label: 'Gwinnett' },
      { value: 'HABERSHAM', label: 'Habersham' },
      { value: 'HALL', label: 'Hall' },
      { value: 'HANCOCK', label: 'Hancock' },
      { value: 'HARALSON', label: 'Haralson' },
      { value: 'HARRIS', label: 'Harris' },
      { value: 'HART', label: 'Hart' },
      { value: 'HEARD', label: 'Heard' },
      { value: 'HENRY', label: 'Henry' },
      { value: 'HOUSTON', label: 'Houston' },
      { value: 'IRWIN', label: 'Irwin' },
      { value: 'JACKSON', label: 'Jackson' },
      { value: 'JASPER', label: 'Jasper' },
      { value: 'JEFF DAVIS', label: 'Jeff Davis' },
      { value: 'JEFFERSON', label: 'Jefferson' },
      { value: 'JENKINS', label: 'Jenkins' },
      { value: 'JOHNSON', label: 'Johnson' },
      { value: 'JONES', label: 'Jones' },
      { value: 'LAMAR', label: 'Lamar' },
      { value: 'LANIER', label: 'Lanier' },
      { value: 'LAURENS', label: 'Laurens' },
      { value: 'LEE', label: 'Lee' },
      { value: 'LIBERTY', label: 'Liberty' },
      { value: 'LINCOLN', label: 'Lincoln' },
      { value: 'LONG', label: 'Long' },
      { value: 'LOWNDES', label: 'Lowndes' },
      { value: 'LUMPKIN', label: 'Lumpkin' },
      { value: 'MACON', label: 'Macon' },
      { value: 'MADISON', label: 'Madison' },
      { value: 'MARION', label: 'Marion' },
      { value: 'MCDUFFIE', label: 'McDuffie' },
      { value: 'MCINTOSH', label: 'McIntosh' },
      { value: 'MERIWETHER', label: 'Meriwether' },
      { value: 'MILLER', label: 'Miller' },
      { value: 'MITCHELL', label: 'Mitchell' },
      { value: 'MONROE', label: 'Monroe' },
      { value: 'MONTGOMERY', label: 'Montgomery' },
      { value: 'MORGAN', label: 'Morgan' },
      { value: 'MURRAY', label: 'Murray' },
      { value: 'MUSCOGEE', label: 'Muscogee' },
      { value: 'NEWTON', label: 'Newton' },
      { value: 'OCONEE', label: 'Oconee' },
      { value: 'OGLETHORPE', label: 'Oglethorpe' },
      { value: 'PAULDING', label: 'Paulding' },
      { value: 'PEACH', label: 'Peach' },
      { value: 'PICKENS', label: 'Pickens' },
      { value: 'PIERCE', label: 'Pierce' },
      { value: 'PIKE', label: 'Pike' },
      { value: 'POLK', label: 'Polk' },
      { value: 'PULASKI', label: 'Pulaski' },
      { value: 'PUTNAM', label: 'Putnam' },
      { value: 'QUITMAN', label: 'Quitman' },
      { value: 'RABUN', label: 'Rabun' },
      { value: 'RANDOLPH', label: 'Randolph' },
      { value: 'RICHMOND', label: 'Richmond' },
      { value: 'ROCKDALE', label: 'Rockdale' },
      { value: 'SCHLEY', label: 'Schley' },
      { value: 'SCREVEN', label: 'Screven' },
      { value: 'SEMINOLE', label: 'Seminole' },
      { value: 'SPALDING', label: 'Spalding' },
      { value: 'STEPHENS', label: 'Stephens' },
      { value: 'STEWART', label: 'Stewart' },
      { value: 'SUMTER', label: 'Sumter' },
      { value: 'TALBOT', label: 'Talbot' },
      { value: 'TALIAFERRO', label: 'Taliaferro' },
      { value: 'TATTNALL', label: 'Tattnall' },
      { value: 'TAYLOR', label: 'Taylor' },
      { value: 'TELFAIR', label: 'Telfair' },
      { value: 'TERRELL', label: 'Terrell' },
      { value: 'THOMAS', label: 'Thomas' },
      { value: 'TIFT', label: 'Tift' },
      { value: 'TOOMBS', label: 'Toombs' },
      { value: 'TOWNS', label: 'Towns' },
      { value: 'TREUTLEN', label: 'Treutlen' },
      { value: 'TROUP', label: 'Troup' },
      { value: 'TURNER', label: 'Turner' },
      { value: 'TWIGGS', label: 'Twiggs' },
      { value: 'UNION', label: 'Union' },
      { value: 'UPSON', label: 'Upson' },
      { value: 'WALKER', label: 'Walker' },
      { value: 'WALTON', label: 'Walton' },
      { value: 'WARE', label: 'Ware' },
      { value: 'WARREN', label: 'Warren' },
      { value: 'WASHINGTON', label: 'Washington' },
      { value: 'WAYNE', label: 'Wayne' },
      { value: 'WEBSTER', label: 'Webster' },
      { value: 'WHEELER', label: 'Wheeler' },
      { value: 'WHITE', label: 'White' },
      { value: 'WHITFIELD', label: 'Whitfield' },
      { value: 'WILCOX', label: 'Wilcox' },
      { value: 'WILKES', label: 'Wilkes' },
      { value: 'WILKINSON', label: 'Wilkinson' },
      { value: 'WORTH', label: 'Worth' }
    ],
    SC: [
      { value: 'ABBEVILLE', label: 'Abbeville' },
      { value: 'AIKEN', label: 'Aiken' },
      { value: 'ALLENDALE', label: 'Allendale' },
      { value: 'ANDERSON', label: 'Anderson' },
      { value: 'BAMBERG', label: 'Bamberg' },
      { value: 'BARNWELL', label: 'Barnwell' },
      { value: 'BEAUFORT', label: 'Beaufort' },
      { value: 'BERKELEY', label: 'Berkeley' },
      { value: 'CALHOUN', label: 'Calhoun' },
      { value: 'CHARLESTON', label: 'Charleston' },
      { value: 'CHEROKEE', label: 'Cherokee' },
      { value: 'CHESTER', label: 'Chester' },
      { value: 'CHESTERFIELD', label: 'Chesterfield' },
      { value: 'CLARENDON', label: 'Clarendon' },
      { value: 'COLLETON', label: 'Colleton' },
      { value: 'DARLINGTON', label: 'Darlington' },
      { value: 'DILLON', label: 'Dillon' },
      { value: 'DORCHESTER', label: 'Dorchester' },
      { value: 'EDGEFIELD', label: 'Edgefield' },
      { value: 'FAIRFIELD', label: 'Fairfield' },
      { value: 'FLORENCE', label: 'Florence' },
      { value: 'GEORGETOWN', label: 'Georgetown' },
      { value: 'GREENVILLE', label: 'Greenville' },
      { value: 'GREENWOOD', label: 'Greenwood' },
      { value: 'HAMPTON', label: 'Hampton' },
      { value: 'HORRY', label: 'Horry' },
      { value: 'JASPER', label: 'Jasper' },
      { value: 'KERSHAW', label: 'Kershaw' },
      { value: 'LANCASTER', label: 'Lancaster' },
      { value: 'LAURENS', label: 'Laurens' },
      { value: 'LEE', label: 'Lee' },
      { value: 'LEXINGTON', label: 'Lexington' },
      { value: 'MARION', label: 'Marion' },
      { value: 'MARLBORO', label: 'Marlboro' },
      { value: 'MCCORMICK', label: 'McCormick' },
      { value: 'NEWBERRY', label: 'Newberry' },
      { value: 'OCONEE', label: 'Oconee' },
      { value: 'ORANGEBURG', label: 'Orangeburg' },
      { value: 'PICKENS', label: 'Pickens' },
      { value: 'RICHLAND', label: 'Richland' },
      { value: 'SALUDA', label: 'Saluda' },
      { value: 'SPARTANBURG', label: 'Spartanburg' },
      { value: 'SUMTER', label: 'Sumter' },
      { value: 'UNION', label: 'Union' },
      { value: 'WILLIAMSBURG', label: 'Williamsburg' },
      { value: 'YORK', label: 'York' }
    ],
    LA: [
      { value: 'ACADIA', label: 'Acadia' },
      { value: 'ALLEN', label: 'Allen' },
      { value: 'ASCENSION', label: 'Ascension' },
      { value: 'ASSUMPTION', label: 'Assumption' },
      { value: 'AVOYELLES', label: 'Avoyelles' },
      { value: 'BEAUREGARD', label: 'Beauregard' },
      { value: 'BIENVILLE', label: 'Bienville' },
      { value: 'BOSSIER', label: 'Bossier' },
      { value: 'CADDO', label: 'Caddo' },
      { value: 'CALCASIEU', label: 'Calcasieu' },
      { value: 'CALDWELL', label: 'Caldwell' },
      { value: 'CAMERON', label: 'Cameron' },
      { value: 'CATAHOULA', label: 'Catahoula' },
      { value: 'CLAIBORNE', label: 'Claiborne' },
      { value: 'CONCORDIA', label: 'Concordia' },
      { value: 'DE SOTO', label: 'De Soto' },
      { value: 'EAST BATON ROUGE', label: 'East Baton Rouge' },
      { value: 'EAST CARROLL', label: 'East Carroll' },
      { value: 'EAST FELICIANA', label: 'East Feliciana' },
      { value: 'EVANGELINE', label: 'Evangeline' },
      { value: 'FRANKLIN', label: 'Franklin' },
      { value: 'GRANT', label: 'Grant' },
      { value: 'IBERIA', label: 'Iberia' },
      { value: 'IBERVILLE', label: 'Iberville' },
      { value: 'JACKSON', label: 'Jackson' },
      { value: 'JEFFERSON', label: 'Jefferson' },
      { value: 'JEFFERSON DAVIS', label: 'Jefferson Davis' },
      { value: 'LA SALLE', label: 'La Salle' },
      { value: 'LAFAYETTE', label: 'Lafayette' },
      { value: 'LAFOURCHE', label: 'Lafourche' },
      { value: 'LINCOLN', label: 'Lincoln' },
      { value: 'LIVINGSTON', label: 'Livingston' },
      { value: 'MADISON', label: 'Madison' },
      { value: 'MOREHOUSE', label: 'Morehouse' },
      { value: 'NATCHITOCHES', label: 'Natchitoches' },
      { value: 'ORLEANS', label: 'Orleans' },
      { value: 'OUACHITA', label: 'Ouachita' },
      { value: 'PLAQUEMINES', label: 'Plaquemines' },
      { value: 'POINTE COUPEE', label: 'Pointe Coupee' },
      { value: 'RAPIDES', label: 'Rapides' },
      { value: 'RED RIVER', label: 'Red River' },
      { value: 'RICHLAND', label: 'Richland' },
      { value: 'SABINE', label: 'Sabine' },
      { value: 'SAINT BERNARD', label: 'Saint Bernard' },
      { value: 'SAINT CHARLES', label: 'Saint Charles' },
      { value: 'SAINT HELENA', label: 'Saint Helena' },
      { value: 'SAINT JAMES', label: 'Saint James' },
      { value: 'SAINT LANDRY', label: 'Saint Landry' },
      { value: 'SAINT MARTIN', label: 'Saint Martin' },
      { value: 'SAINT MARY', label: 'Saint Mary' },
      { value: 'SAINT TAMMANY', label: 'Saint Tammany' },
      { value: 'ST JOHN THE BAPTIST', label: 'St John the Baptist' },
      { value: 'TANGIPAHOA', label: 'Tangipahoa' },
      { value: 'TENSAS', label: 'Tensas' },
      { value: 'TERREBONNE', label: 'Terrebonne' },
      { value: 'UNION', label: 'Union' },
      { value: 'VERMILION', label: 'Vermilion' },
      { value: 'VERNON', label: 'Vernon' },
      { value: 'WASHINGTON', label: 'Washington' },
      { value: 'WEBSTER', label: 'Webster' },
      { value: 'WEST BATON ROUGE', label: 'West Baton Rouge' },
      { value: 'WEST CARROLL', label: 'West Carroll' },
      { value: 'WEST FELICIANA', label: 'West Feliciana' },
      { value: 'WINN', label: 'Winn' }
    ]
  };

  const handlePolicyChange = (field, value) => {
    if (field.includes('.')) {
      const keys = field.split('.');

      // Функция для создания вложенного объекта
      const createNestedUpdate = (obj, keys, value) => {
        if (keys.length === 1) {
          return { ...obj, [keys[0]]: value };
        }

        const [currentKey, ...remainingKeys] = keys;
        return {
          ...obj,
          [currentKey]: createNestedUpdate(obj[currentKey] || {}, remainingKeys, value)
        };
      };

      setFormData({
        ...formData,
        policy: createNestedUpdate(formData.policy, keys, value)
      });
    } else {
      setFormData({
        ...formData,
        policy: {
          ...formData.policy,
          [field]: value
        }
      });
    }
  };

  // Функции для работы с локациями
  const handleLocationChange = (locationId, field, value) => {
    console.log('handleLocationChange called:', { locationId, field, value });

    // Создаем обновленные данные локации
    const currentLocation = formData.account.location[locationId];
    console.log('Current location:', currentLocation);

    const updatedLocation = {
      ...currentLocation,
      [field]: value
    };

    // При изменении штата сбрасываем county, если он не подходит для нового штата
    if (field === 'state') {
      const currentCounty = currentLocation.county;
      const newStateCounties = COUNTIES_BY_STATE[value] || [];
      const countyExists = newStateCounties.some(county => county.value === currentCounty);

      if (!countyExists) {
        updatedLocation.county = '';
      }
    }

    console.log('Updated location:', updatedLocation);

    // Создаем новые данные формы
    const newFormData = {
      ...formData,
      account: {
        ...formData.account,
        location: {
          ...formData.account.location,
          [locationId]: updatedLocation
        }
      }
    };

    console.log('Setting new formData, location will be:', newFormData.account.location[locationId]);
    setFormData(newFormData);

    // Принудительно обновляем компонент
    setForceUpdate(prev => prev + 1);
  };

  // Функция для обновления штата полиса и всех локаций
  const handlePolicyStateChange = (newState) => {
    console.log('Policy state changing to:', newState);

    setFormData(prevData => {
      const newLocations = {};

      // Обновляем штат для всех существующих локаций
      Object.keys(prevData.account.location).forEach(locationId => {
        const currentLocation = prevData.account.location[locationId];
        newLocations[locationId] = {
          ...currentLocation,
          state: newState,
          county: '' // Сбрасываем county при смене штата
        };
      });

      return {
        ...prevData,
        policy: {
          ...prevData.policy,
          state: newState
        },
        account: {
          ...prevData.account,
          location: newLocations
        }
      };
    });

    // Принудительно обновляем компонент
    setForceUpdate(prev => prev + 1);
  };

  const handleAddLocation = () => {
    const newLocationId = `id${Date.now()}`;
    const locationIdNum = parseInt(newLocationId.replace('id', ''));
    setFormData(prevData => ({
      ...prevData,
      account: {
        ...prevData.account,
        location: {
          ...prevData.account.location,
          [newLocationId]: {
            county: '',
            state: prevData.policy.state || '', // Берем штат из policy
            extraData: {
              idOriginal: locationIdNum
            }
          }
        }
      }
    }));

    // Принудительно обновляем компонент
    setForceUpdate(prev => prev + 1);
  };

  const handleRemoveLocation = (locationId) => {
    const { [locationId]: removedLocation, ...remainingLocations } = formData.account.location;

    // Также удаляем все риски, связанные с этой локацией
    const remainingRisks = {};
    Object.keys(formData.policy.lineGL.risk).forEach(riskKey => {
      const risk = formData.policy.lineGL.risk[riskKey];
      if (risk.extraData.locationId.toString() !== locationId.replace('id', '')) {
        remainingRisks[riskKey] = risk;
      }
    });

    setFormData({
      ...formData,
      account: {
        ...formData.account,
        location: remainingLocations
      },
      policy: {
        ...formData.policy,
        lineGL: {
          ...formData.policy.lineGL,
          risk: remainingRisks
        }
      }
    });
  };

  // Функция для обработки изменения лимитов
  const handleLimitCombinationChange = (combinationIndex) => {
    const combination = GL_LIMIT_COMBINATIONS[combinationIndex];
    if (combination) {
      setFormData({
        ...formData,
        policy: {
          ...formData.policy,
          lineGL: {
            ...formData.policy.lineGL,
            policyPerOccurrenceLimit: combination.perOccurrence,
            policyAggregateLimit: combination.aggregate,
            productCompletedOperationLimit: combination.aggregate,
            personalAdvertisingInjuryLimit: combination.perOccurrence
          }
        }
      });
    }
  };

  // Получить текущую комбинацию лимитов
  const getCurrentLimitCombination = () => {
    const currentPerOccurrence = formData.policy.lineGL.policyPerOccurrenceLimit;
    const currentAggregate = formData.policy.lineGL.policyAggregateLimit;

    return GL_LIMIT_COMBINATIONS.findIndex(combo =>
      combo.perOccurrence === currentPerOccurrence && combo.aggregate === currentAggregate
    );
  };

  // Функции для работы с рисками
  const handleRiskChange = (riskKey, field, value) => {
    setFormData({
      ...formData,
      policy: {
        ...formData.policy,
        lineGL: {
          ...formData.policy.lineGL,
          risk: {
            ...formData.policy.lineGL.risk,
            [riskKey]: {
              ...formData.policy.lineGL.risk[riskKey],
              [field]: value
            }
          }
        }
      }
    });
  };

  const handleAddRisk = (locationId) => {
    const locationIdNum = parseInt(locationId.replace('id', ''));
    const existingRisks = Object.keys(formData.policy.lineGL.risk).filter(key =>
      formData.policy.lineGL.risk[key].extraData.locationId === locationIdNum
    );
    const riskNumber = existingRisks.length + 1;
    const newRiskKey = `91341_${locationIdNum}_${riskNumber}`;

    setFormData({
      ...formData,
      policy: {
        ...formData.policy,
        lineGL: {
          ...formData.policy.lineGL,
          risk: {
            ...formData.policy.lineGL.risk,
            [newRiskKey]: {
              code: 91341,
              exposure: 5000,
              extraData: {
                locationId: locationIdNum
              }
            }
          }
        }
      }
    });
  };

  const handleRemoveRisk = (riskKey) => {
    const { [riskKey]: removedRisk, ...remainingRisks } = formData.policy.lineGL.risk;

    setFormData({
      ...formData,
      policy: {
        ...formData.policy,
        lineGL: {
          ...formData.policy.lineGL,
          risk: remainingRisks
        }
      }
    });
  };

  const handleSpecialCrDrChange = (field, value) => {
    setFormData({
      ...formData,
      policy: {
        ...formData.policy,
        specialCrDr: {
          ...formData.policy.specialCrDr,
          [field]: value
        }
      }
    });
  };

  // Функция для расчета общей суммы Credit/Debit
  const calculateSpecialCrDrTotal = () => {
    const specialCrDr = formData.policy.specialCrDr || {};
    const management = parseFloat(specialCrDr.management || 0);
    const safetyPrograms = parseFloat(specialCrDr.safetyPrograms || 0);
    const supervisionEmployeeExperience = parseFloat(specialCrDr.supervisionEmployeeExperience || 0);
    const specialCharacteristics = parseFloat(specialCrDr.specialCharacteristics || 0);

    return management + safetyPrograms + supervisionEmployeeExperience + specialCharacteristics;
  };

  // Функции для работы с Additional Insured
  const getAdditionalInsuredList = () => {
    const ais = formData.policy.lineGL.optionalCoverages?.additionalInsured || {};
    return Object.keys(ais).map(aiKey => ({
      key: aiKey,
      ...ais[aiKey]
    }));
  };

  const handleAddAdditionalInsured = () => {
    const existingAIs = Object.keys(formData.policy.lineGL.optionalCoverages?.additionalInsured || {});
    const nextNumber = existingAIs.length + 1;
    const newAiKey = `ai${nextNumber}`;

    setFormData(prevData => ({
      ...prevData,
      policy: {
        ...prevData.policy,
        lineGL: {
          ...prevData.policy.lineGL,
          optionalCoverages: {
            ...prevData.policy.lineGL.optionalCoverages,
            additionalInsured: {
              ...prevData.policy.lineGL.optionalCoverages?.additionalInsured,
              [newAiKey]: {
                form: 'CG2010',
                waiver: false
              }
            }
          }
        }
      }
    }));
  };

  const handleRemoveAdditionalInsured = (aiKey) => {
    const { [aiKey]: removedAI, ...remainingAIs } = formData.policy.lineGL.optionalCoverages?.additionalInsured || {};

    setFormData({
      ...formData,
      policy: {
        ...formData.policy,
        lineGL: {
          ...formData.policy.lineGL,
          optionalCoverages: {
            ...formData.policy.lineGL.optionalCoverages,
            additionalInsured: remainingAIs
          }
        }
      }
    });
  };

  const handleAdditionalInsuredChange = (aiKey, field, value) => {
    setFormData({
      ...formData,
      policy: {
        ...formData.policy,
        lineGL: {
          ...formData.policy.lineGL,
          optionalCoverages: {
            ...formData.policy.lineGL.optionalCoverages,
            additionalInsured: {
              ...formData.policy.lineGL.optionalCoverages?.additionalInsured,
              [aiKey]: {
                ...formData.policy.lineGL.optionalCoverages?.additionalInsured?.[aiKey],
                [field]: value
              }
            }
          }
        }
      }
    });
  };

  // Функции для работы с GL Miscellaneous покрытиями
  const handleGLMiscellaneousChange = (coverage, checked) => {
    const coverageData = checked ? {
      name: coverage,
      value: 'yes'
    } : null;

    setFormData({
      ...formData,
      policy: {
        ...formData.policy,
        lineGL: {
          ...formData.policy.lineGL,
          optionalCoverages: {
            ...formData.policy.lineGL.optionalCoverages,
            Miscellaneous: {
              ...(formData.policy.lineGL.optionalCoverages?.Miscellaneous || {}),
              [coverage]: coverageData
            }
          }
        }
      }
    });
  };

  // Функции для работы с Inland Marine
  const handleIMCoverageToggle = (coverage, checked) => {
    const newCoverages = { ...(formData.policy.lineIM?.coverages || {}) };

    if (checked) {
      if (coverage === 'contractorsEquipmentScheduled') {
        newCoverages[coverage] = { items: {} };
      } else if (coverage === 'contractorsEquipmentMiscTools') {
        newCoverages[coverage] = { limit: 5000 };
      } else if (coverage === 'installationFloater') {
        newCoverages[coverage] = { limit: 5000 };
      } else if (coverage === 'contractorsInstallationFloater') {
        newCoverages[coverage] = { limit: 5000 };
      }
    } else {
      delete newCoverages[coverage];
    }

    setFormData({
      ...formData,
      policy: {
        ...formData.policy,
        lineIM: {
          ...formData.policy.lineIM,
          coverages: newCoverages
        }
      }
    });
  };

  const handleIMCoverageChange = (coverage, field, value) => {
    setFormData({
      ...formData,
      policy: {
        ...formData.policy,
        lineIM: {
          ...formData.policy.lineIM,
          coverages: {
            ...(formData.policy.lineIM?.coverages || {}),
            [coverage]: {
              ...(formData.policy.lineIM?.coverages?.[coverage] || {}),
              [field]: value
            }
          }
        }
      }
    });
  };

  // Функции для Scheduled Equipment Items
  const handleAddScheduledItem = () => {
    const existingItems = Object.keys(formData.policy.lineIM?.coverages?.contractorsEquipmentScheduled?.items || {});
    const nextNumber = existingItems.length + 1;
    const newItemKey = `item${nextNumber}`;

    setFormData({
      ...formData,
      policy: {
        ...formData.policy,
        lineIM: {
          ...formData.policy.lineIM,
          coverages: {
            ...(formData.policy.lineIM?.coverages || {}),
            contractorsEquipmentScheduled: {
              ...(formData.policy.lineIM?.coverages?.contractorsEquipmentScheduled || {}),
              items: {
                ...(formData.policy.lineIM?.coverages?.contractorsEquipmentScheduled?.items || {}),
                [newItemKey]: {
                  description: '',
                  limit: 10000
                }
              }
            }
          }
        }
      }
    });
  };

  const handleRemoveScheduledItem = (itemKey) => {
    const { [itemKey]: removedItem, ...remainingItems } = formData.policy.lineIM?.coverages?.contractorsEquipmentScheduled?.items || {};

    setFormData({
      ...formData,
      policy: {
        ...formData.policy,
        lineIM: {
          ...formData.policy.lineIM,
          coverages: {
            ...(formData.policy.lineIM?.coverages || {}),
            contractorsEquipmentScheduled: {
              ...(formData.policy.lineIM?.coverages?.contractorsEquipmentScheduled || {}),
              items: remainingItems
            }
          }
        }
      }
    });
  };

  const handleScheduledItemChange = (itemKey, field, value) => {
    setFormData({
      ...formData,
      policy: {
        ...formData.policy,
        lineIM: {
          ...formData.policy.lineIM,
          coverages: {
            ...(formData.policy.lineIM?.coverages || {}),
            contractorsEquipmentScheduled: {
              ...(formData.policy.lineIM?.coverages?.contractorsEquipmentScheduled || {}),
              items: {
                ...(formData.policy.lineIM?.coverages?.contractorsEquipmentScheduled?.items || {}),
                [itemKey]: {
                  ...(formData.policy.lineIM?.coverages?.contractorsEquipmentScheduled?.items?.[itemKey] || {}),
                  [field]: field === 'limit' ? parseInt(value) || 0 : value
                }
              }
            }
          }
        }
      }
    });
  };

  const getScheduledItemsList = () => {
    const items = formData.policy.lineIM?.coverages?.contractorsEquipmentScheduled?.items || {};
    return Object.keys(items).map(itemKey => ({
      key: itemKey,
      ...items[itemKey]
    }));
  };

  const handleLoadTemplate = (templateData) => {
    // Проверяем, есть ли в темплейте версия
    if (templateData.versionNumber) {
      setVersionNumber(templateData.versionNumber);
    }
    // Загружаем остальные данные формы
    const { versionNumber, ...formDataOnly } = templateData;

    // Автоматически пересчитываем Expiration Date на основе Effective Date
    if (formDataOnly.policy?.effectiveDate) {
      formDataOnly.policy.expirationDate = calculateExpirationDate(formDataOnly.policy.effectiveDate);
    }

    setFormData(formDataOnly);
    setResult(null);
    setError(null);
    setLastRequest(null);
  };

  const formatDataForAPI = () => {
    // Конвертируем данные в формат, ожидаемый API
    const apiData = {
      account: formData.account,
      policy: {
        ...formData.policy,
        lineGL: {
          ...formData.policy.lineGL,
          optionalCoverages: {
            ...formData.policy.lineGL.optionalCoverages,
            // Конвертируем additionalInsured в правильный формат с заглавной буквы
            AdditionalInsured: formData.policy.lineGL.optionalCoverages?.additionalInsured || {}
          }
        }
      }
    };

    // Удаляем старый формат если он есть
    if (apiData.policy.lineGL.optionalCoverages.additionalInsured) {
      delete apiData.policy.lineGL.optionalCoverages.additionalInsured;
    }


    return apiData;
  };

  const [lastRequest, setLastRequest] = useState(null);
  const [sectionsExpanded, setSectionsExpanded] = useState({
    quoteParameters: true,
    requestResponse: false,
    calculationResults: true
  });

  const expandAllSections = () => {
    setSectionsExpanded({
      quoteParameters: true,
      requestResponse: true,
      calculationResults: true
    });
  };

  const collapseAllSections = () => {
    setSectionsExpanded({
      quoteParameters: false,
      requestResponse: false,
      calculationResults: false
    });
  };

  const toggleSection = (section) => {
    setSectionsExpanded(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const apiData = formatDataForAPI();
      const fullRequest = {
        version: {
          type: '13',
          number: versionNumber
        },
        ...apiData
      };

      setLastRequest(fullRequest);
      const response = await calculatePremium('13', versionNumber, apiData);

      if (response.success) {
        setResult(response.data);
        // Разворачиваем секцию Calculation Results после успешного расчета
        setSectionsExpanded(prev => ({
          ...prev,
          calculationResults: true
        }));
      } else {
        setError(response.error);
      }
    } catch (err) {
      setError('An unexpected error occurred: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateResultsHTML = (result, timestamp) => {
    if (!result || result._status !== 'ok') {
      return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Artisan Beazley Calculation Results - ${timestamp}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 20px; background-color: #f5f5f5; }
        .container { max-width: 1200px; margin: 0 auto; background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        .header { border-bottom: 2px solid #007bff; padding-bottom: 15px; margin-bottom: 20px; }
        .error { background-color: #f8d7da; color: #721c24; padding: 15px; border-radius: 4px; border: 1px solid #f5c6cb; }
        .timestamp { color: #666; font-size: 0.9em; }
        pre { background-color: #f8f9fa; padding: 15px; border-radius: 4px; overflow-x: auto; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Artisan Beazley Calculation Results</h1>
            <div class="timestamp">Generated: ${new Date().toLocaleString()}</div>
        </div>
        <div class="error">
            <strong>Error:</strong> Unable to generate detailed results. Please check the response data.
        </div>
        <h3>Raw Response</h3>
        <pre>${JSON.stringify(result, null, 2)}</pre>
    </div>
</body>
</html>`;
    }

    const premium = result.policy?.lineGL?._rated?.premium;
    const premiumMinimum = result.policy?.lineGL?._rated?.premiumMinimum;

    // Generate risk details HTML
    let riskDetailsHTML = '';
    if (result.policy?.lineGL?.risk) {
      riskDetailsHTML = Object.keys(result.policy.lineGL.risk).map(riskKey => {
        const risk = result.policy.lineGL.risk[riskKey];
        const locationId = risk.extraData?.locationId;
        const location = result.account?.location?.[`id${locationId}`];

        return `
        <div style="border: 1px solid #ddd; margin: 15px 0; padding: 15px; border-radius: 6px; background-color: #f8f9fa;">
          <h5>Risk: ${riskKey}</h5>
          ${location ? `<p><strong>Location:</strong> ${location.county}, ${location.state} (ID: ${locationId})</p>` : ''}
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
            <div>
              <p><strong>Class Code:</strong> ${risk.code}</p>
              <p><strong>Exposure (Payroll):</strong> $${risk.exposure?.toLocaleString()}</p>
            </div>
            <div>
              ${risk._rated ? `
                <p><strong>Premium:</strong> $${parseFloat(risk._rated.premium || 0).toFixed(2)}</p>
                <p><strong>Rate:</strong> ${risk._rated.rates?.rate?.toFixed(4) || 'N/A'}%</p>
              ` : ''}
            </div>
          </div>
          ${risk._rated?.rates ? `
          <details style="margin-top: 10px;">
            <summary><strong>Rating Details</strong></summary>
            <div style="padding: 10px; background: white; border-radius: 4px; margin-top: 10px;">
              ${risk._rated.rates.territory ? `<p><strong>Territory:</strong> ${risk._rated.rates.territory}</p>` : ''}
              ${risk._rated.rates.basisFactor ? `<p><strong>Basis Factor:</strong> ${risk._rated.rates.basisFactor}</p>` : ''}
              ${risk._rated.rates.singleAggregateLimit ? `<p><strong>Single Aggregate Limit Factor:</strong> ${risk._rated.rates.singleAggregateLimit}</p>` : ''}
            </div>
          </details>
          ` : ''}
        </div>`;
      }).join('');
    }

    // Generate optional coverages HTML
    let optionalCoveragesHTML = '';
    if (result.policy?.optionalCoverages?.miscellaneous?.terrorism?._rated) {
      optionalCoveragesHTML += `
      <div style="border: 1px solid #ddd; margin: 10px 0; padding: 10px; border-radius: 4px; background-color: #f8f9fa;">
        <h5>TRIA Terrorism Coverage</h5>
        <p><strong>Premium:</strong> $${parseFloat(result.policy.optionalCoverages.miscellaneous.terrorism._rated.premium || 0).toFixed(2)}</p>
        <p><strong>Rate:</strong> ${result.policy.optionalCoverages.miscellaneous.terrorism._rated.rates?.tria || 'N/A'}</p>
      </div>`;
    }

    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Artisan Beazley Calculation Results - ${timestamp}</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
            margin: 20px;
            background-color: #f5f5f5;
            line-height: 1.6;
        }
        .container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
            padding: 30px;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .header {
            border-bottom: 2px solid #007bff;
            padding-bottom: 15px;
            margin-bottom: 30px;
        }
        .header h1 {
            color: #007bff;
            margin: 0 0 10px 0;
        }
        .timestamp {
            color: #666;
            font-size: 0.9em;
        }
        .summary-box {
            background-color: #e7f3ff;
            border: 1px solid #b3d7ff;
            border-radius: 6px;
            padding: 20px;
            margin-bottom: 30px;
        }
        .summary-box h4 {
            margin-top: 0;
            color: #0056b3;
        }
        .card {
            border: 1px solid #ddd;
            border-radius: 6px;
            padding: 20px;
            margin-bottom: 20px;
            background-color: #fafafa;
        }
        .card h4 {
            margin-top: 0;
            color: #495057;
        }
        .grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
        }
        pre {
            background-color: #f8f9fa;
            padding: 20px;
            border-radius: 6px;
            overflow-x: auto;
            font-size: 0.9em;
            border: 1px solid #e9ecef;
        }
        details {
            margin: 15px 0;
        }
        summary {
            cursor: pointer;
            font-weight: 500;
            padding: 5px;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Artisan Beazley Calculation Results</h1>
            <div class="timestamp">Generated: ${new Date().toLocaleString()}</div>
        </div>

        <div class="summary-box">
            <h4>Policy Summary</h4>
            <div class="grid">
                <div>
                    <p><strong>Total Premium:</strong> $${parseFloat(premium || 0).toFixed(2)}</p>
                    <p><strong>Premium Minimum:</strong> $${parseFloat(premiumMinimum || 0).toFixed(2)}</p>
                </div>
                <div>
                    <p><strong>Policy State:</strong> ${result.policy?.state || 'N/A'}</p>
                    <p><strong>Company:</strong> ${result.policy?.company || 'N/A'}</p>
                </div>
            </div>
        </div>

        <div class="card">
            <h4>Coverage Limits</h4>
            <div class="grid">
                <div>
                    <p><strong>Per Occurrence:</strong> $${result.policy?.lineGL?.policyPerOccurrenceLimit?.toLocaleString() || result.policy?.lineGL?.PolicyPerOccurenceLimit?.toLocaleString() || 'N/A'}</p>
                    <p><strong>Aggregate:</strong> $${result.policy?.lineGL?.policyAggregateLimit?.toLocaleString() || result.policy?.lineGL?.PolicyAggregateLimit?.toLocaleString() || 'N/A'}</p>
                </div>
                <div>
                    <p><strong>Products Completed Ops:</strong> $${result.policy?.lineGL?.productCompletedOperationLimit?.toLocaleString() || result.policy?.lineGL?.ProductCompletedOperationLimit?.toLocaleString() || 'N/A'}</p>
                    <p><strong>Personal & Advertising:</strong> $${result.policy?.lineGL?.personalAdvertisingInjuryLimit?.toLocaleString() || result.policy?.lineGL?.PersonalAdvertisingInjuryLimit?.toLocaleString() || 'N/A'}</p>
                </div>
            </div>
            <p><strong>Property Damage Deductible:</strong> $${result.policy?.lineGL?.deductibles?.pd?.toLocaleString() || result.policy?.lineGL?.Deductibles?.PD?.toLocaleString() || '0'}</p>
        </div>

        ${riskDetailsHTML ? `
        <div class="card">
            <h4>Risk Details by Location & Class Code</h4>
            ${riskDetailsHTML}
        </div>
        ` : ''}

        ${optionalCoveragesHTML ? `
        <div class="card">
            <h4>Optional Coverages</h4>
            ${optionalCoveragesHTML}
        </div>
        ` : ''}

        ${result.policy?._rated ? `
        <div class="card">
            <h4>Fees & Inspection</h4>
            <div class="grid">
                ${result.policy._rated.policyfee?.value !== undefined ? `<p><strong>Policy Fee:</strong> $${parseFloat(result.policy._rated.policyfee.value || 0).toFixed(2)}</p>` : ''}
                ${result.policy._rated.inspection !== undefined ? `<p><strong>Inspection Fee:</strong> $${parseFloat(result.policy._rated.inspection || 0).toFixed(2)}</p>` : ''}
                ${result.policy._rated.fee !== undefined ? `<p><strong>Total Fees:</strong> $${parseFloat(result.policy._rated.fee || 0).toFixed(2)}</p>` : ''}
            </div>
            ${result.account?.location && Object.keys(result.account.location).some(locKey => result.account.location[locKey]._rated?.inspection > 0) ? `
            <div style="margin-top: 15px; padding: 10px; background-color: #f8f9fa; border-radius: 4px;">
                <h5>Inspection Fee by Location</h5>
                ${Object.keys(result.account.location).map(locationKey => {
                  const location = result.account.location[locationKey];
                  if (location._rated?.inspection > 0) {
                    return `<p><strong>${location.county}, ${location.state}:</strong> $${parseFloat(location._rated.inspection || 0).toFixed(2)}</p>`;
                  }
                  return '';
                }).join('')}
            </div>
            ` : ''}
        </div>
        ` : ''}

        <details>
            <summary>View Full Response JSON</summary>
            <pre style="background-color: #f8f9fa; padding: 15px; border-radius: 4px; overflow-x: auto; font-size: 12px; line-height: 1.4;">${JSON.stringify(result, null, 2)}</pre>
        </details>
    </div>
</body>
</html>`;
  };

  const downloadZipArchive = async (requestData, responseData, timestamp, isError = false) => {
    const zip = new JSZip();

    // Добавляем request JSON в архив
    const requestJson = JSON.stringify(requestData, null, 2);
    zip.file('request.json', requestJson);

    // Добавляем response или error JSON в архив
    if (isError) {
      const errorJson = JSON.stringify({ error: responseData }, null, 2);
      zip.file('error.json', errorJson);
    } else {
      const responseJson = JSON.stringify(responseData, null, 2);
      zip.file('response.json', responseJson);

      // Генерируем HTML файл с результатами (только при успешном расчете)
      const resultsHTML = generateResultsHTML(responseData, timestamp);
      zip.file('results.html', resultsHTML);
    }

    // Создаем import.json для кнопки Import
    const importTemplate = [
      {
        id: `calculation-${timestamp}`,
        name: `Calculation ${timestamp}`,
        createdAt: new Date().toISOString(),
        data: {
          ...formData,
          versionNumber: versionNumber
        }
      }
    ];
    const importJson = JSON.stringify(importTemplate, null, 2);
    zip.file('import.json', importJson);

    // Генерируем ZIP файл и скачиваем его
    const content = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(content);
    const a = document.createElement('a');
    a.href = url;
    a.download = `artisan-evolve-calculation${isError ? '-error' : ''}-${timestamp}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCalculateAndDownload = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    // Генерируем timestamp один раз для всех случаев
    const now = new Date();
    const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);

    try {
      const apiData = formatDataForAPI();
      const fullRequest = {
        version: {
          type: '13',
          number: versionNumber
        },
        ...apiData
      };

      setLastRequest(fullRequest);
      const response = await calculatePremium('13', versionNumber, apiData);

      if (response.success) {
        setResult(response.data);
        // Разворачиваем секцию Calculation Results после успешного расчета
        setSectionsExpanded(prev => ({
          ...prev,
          calculationResults: true
        }));

        // Скачиваем ZIP архив с request.json и response.json
        await downloadZipArchive(fullRequest, response.data, timestamp, false);
      } else {
        setError(response.error);

        // Скачиваем ZIP архив с request.json и error.json
        await downloadZipArchive(fullRequest, response.error, timestamp, true);
      }
    } catch (err) {
      const errorMessage = 'An unexpected error occurred: ' + err.message;
      setError(errorMessage);

      // Скачиваем ZIP архив с request.json и error.json даже при исключении
      const apiData = formatDataForAPI();
      const fullRequest = {
        version: {
          type: '13',
          number: versionNumber
        },
        ...apiData
      };

      await downloadZipArchive(fullRequest, errorMessage, timestamp, true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', flexWrap: 'wrap', gap: '15px' }}>
        <div>
          <h1 style={{ marginBottom: '5px' }}>Artisan Beazley Calculator</h1>
          <p className="text-muted" style={{ marginBottom: '0' }}>General Liability premium calculator for contractors</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 'bold', fontSize: '14px' }}>Section Controls:</span>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={expandAllSections}
            style={{ minWidth: '120px' }}
          >
            ⊞ Expand All
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={collapseAllSections}
            style={{ minWidth: '120px' }}
          >
            ⊟ Collapse All
          </button>
          <button
            type="button"
            className="btn btn-outline-info btn-sm"
            onClick={() => setShowDocumentation(true)}
            style={{ minWidth: '120px' }}
          >
            📖 API Docs
          </button>
        </div>
      </div>

      <TemplateManager
        currentData={{ ...formData, versionNumber }}
        onLoadTemplate={handleLoadTemplate}
        templateType="artisan-evolve"
      />

      <form onSubmit={handleSubmit}>
        <details open={sectionsExpanded.quoteParameters}>
          <summary
            style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '18px', marginBottom: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}
            onClick={(e) => {
              e.preventDefault();
              toggleSection('quoteParameters');
            }}
          >
            Quote Parameters
          </summary>
          <div className="card" style={{ marginTop: '10px' }}>

            <details open>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', marginBottom: '15px' }}>
                Configuration
              </summary>
              <div style={{ marginLeft: '15px' }}>
                <div className="form-group">
                  <label>API Version Number</label>
                  <input
                    type="text"
                    className="form-control"
                    value={versionNumber}
                    onChange={(e) => setVersionNumber(e.target.value)}
                    placeholder="e.g., 1.0.0"
                    pattern="^\d+\.\d+\.\d+(-.*)?$"
                    title="Version must be in format X.Y.Z or X.Y.Z-label"
                  />
                  <small className="text-muted">
                    Format: major.minor.patch (e.g., 1.0.0) or with label (e.g., 1.0.0-beta)
                  </small>
                </div>
              </div>
            </details>

            <details open>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', marginBottom: '15px' }}>
                Policy Information
              </summary>
              <div style={{ marginLeft: '15px' }}>

                <div className="grid">
                  <div className="form-group">
                    <label>State * (All locations must be in this state)</label>
                    <select
                      className="form-control"
                      value={formData.policy.state}
                      onChange={(e) => {
                        const newState = e.target.value;
                        handlePolicyStateChange(newState);
                      }}
                      required
                    >
                      <option value="">Select state...</option>
                      {ARTISAN_STATES.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Effective Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.policy.effectiveDate}
                      onChange={(e) => handleEffectiveDateChange(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Expiration Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.policy.expirationDate}
                      disabled
                      style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                    />
                    <small className="form-text text-muted">
                      Automatically set to +1 year from Effective Date
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Rate Type *</label>
                    <select
                      className="form-control"
                      value={formData.policy.rateType}
                      onChange={(e) => handlePolicyChange('rateType', e.target.value)}
                      required
                    >
                      <option value="NB">New Business</option>
                      <option value="RN">Renewal</option>
                    </select>
                  </div>
                </div>
              </div>
            </details>

            <details open>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', marginBottom: '15px' }}>
                Locations
              </summary>
              <div style={{ marginLeft: '15px' }}>
                <div className="flex-between mb-3">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAddLocation}
                  >
                    Add Location
                  </button>
                </div>

                {getLocationsList().map((location, index) => (
                  <div key={`${location.id}-${location.state}-${forceUpdate}`} className="card" style={{ marginBottom: '20px', padding: '15px' }}>
                    <div className="flex-between mb-2">
                      <h4>Location {index + 1} (state: {location.state}, county: {location.county})</h4>
                      {getLocationsList().length > 1 && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => handleRemoveLocation(location.id)}
                        >
                          Remove Location
                        </button>
                      )}
                    </div>

                    <div className="grid">
                      <div className="form-group">
                        <label>State</label>
                        <input
                          type="text"
                          className="form-control"
                          value={ARTISAN_STATES.find(s => s.value === location.state)?.label || 'Not selected'}
                          disabled
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>

                      <div className="form-group">
                        <label>County *</label>
                        <select
                          className="form-control"
                          value={location.county}
                          onChange={(e) => handleLocationChange(location.id, 'county', e.target.value)}
                          required
                          disabled={!formData.policy.state}
                        >
                          <option value="">Select county...</option>
                          {(COUNTIES_BY_STATE[formData.policy.state] || [])
                            .sort((a, b) => a.label.localeCompare(b.label))
                            .map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>

                    {/* Class Codes для этой локации */}
                    <div className="mt-3">
                      <div className="flex-between mb-2">
                        <h5>Class Codes for this Location</h5>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleAddRisk(location.id)}
                        >
                          Add Class Code
                        </button>
                      </div>

                      {getRisksList()
                        .filter(risk => risk.extraData.locationId === parseInt(location.id.replace('id', '')))
                        .map((risk) => (
                          <div key={risk.key} className="card" style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f8f9fa' }}>
                            <div className="flex-between mb-2">
                              <strong>Risk #{risk.key}</strong>
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => handleRemoveRisk(risk.key)}
                              >
                                Remove
                              </button>
                            </div>

                            <div className="grid">
                              <div className="form-group">
                                <label>Class Code *</label>
                                <SearchableSelect
                                  options={CLASS_CODES}
                                  value={risk.code}
                                  onChange={(value) => handleRiskChange(risk.key, 'code', value)}
                                  placeholder="Select class code..."
                                  required
                                />
                              </div>

                              <div className="form-group">
                                <label>Annual Payroll (Exposure) * (minimum $5,000)</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={risk.exposure}
                                  onChange={(e) => handleRiskChange(risk.key, 'exposure', parseInt(e.target.value) || 0)}
                                  placeholder="5000"
                                  min="5000"
                                  required
                                />
                              </div>
                            </div>
                          </div>
                        ))}

                      {getRisksList().filter(risk => risk.extraData.locationId === parseInt(location.id.replace('id', ''))).length === 0 && (
                        <div className="text-muted" style={{ padding: '10px', textAlign: 'center' }}>
                          No class codes added for this location. Click "Add Class Code" to add one.
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {getLocationsList().length === 0 && (
                  <div className="text-muted" style={{ padding: '20px', textAlign: 'center' }}>
                    No locations added. Click "Add Location" to start.
                  </div>
                )}
              </div>
            </details>

            <details open>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', marginBottom: '15px' }}>
                Coverage Limits
              </summary>
              <div style={{ marginLeft: '15px' }}>

                <div className="form-group">
                  <label>Coverage Limits (Per Occurrence / Aggregate) *</label>
                  <select
                    className="form-control"
                    value={getCurrentLimitCombination()}
                    onChange={(e) => handleLimitCombinationChange(parseInt(e.target.value))}
                    required
                  >
                    <option value="">Select limit combination...</option>
                    {GL_LIMIT_COMBINATIONS.map((combo, index) => (
                      <option key={index} value={index}>
                        {combo.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-text text-muted">
                    Products Completed Operations and Personal & Advertising Injury limits will be set automatically
                  </small>
                </div>

                <div className="grid" style={{ marginTop: '15px' }}>
                  <div className="form-group">
                    <label>Per Occurrence Limit</label>
                    <input
                      type="text"
                      className="form-control"
                      value={`$${formData.policy.lineGL.policyPerOccurrenceLimit?.toLocaleString() || 'Not selected'}`}
                      disabled
                    />
                  </div>

                  <div className="form-group">
                    <label>Aggregate Limit</label>
                    <input
                      type="text"
                      className="form-control"
                      value={`$${formData.policy.lineGL.policyAggregateLimit?.toLocaleString() || 'Not selected'}`}
                      disabled
                    />
                  </div>

                  <div className="form-group">
                    <label>Products Completed Operations Limit</label>
                    <input
                      type="text"
                      className="form-control"
                      value={`$${formData.policy.lineGL.productCompletedOperationLimit?.toLocaleString() || 'Not selected'}`}
                      disabled
                    />
                  </div>

                  <div className="form-group">
                    <label>Personal & Advertising Injury Limit</label>
                    <input
                      type="text"
                      className="form-control"
                      value={`$${formData.policy.lineGL.personalAdvertisingInjuryLimit?.toLocaleString() || 'Not selected'}`}
                      disabled
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Property Damage Deductible</label>
                  <SearchableSelect
                    options={DEDUCTIBLES}
                    value={formData.policy.lineGL.deductibles?.pd || 0}
                    onChange={(value) => handlePolicyChange('lineGL.deductibles.pd', value)}
                    placeholder="Select deductible..."
                  />
                </div>
              </div>
            </details>

            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', marginBottom: '15px' }}>
                Loss History & Previous Insurance
              </summary>
              <div style={{ marginLeft: '15px' }}>

                <div className="form-group">
                  <label>Prior Claims in Last 3 Years</label>
                  <select
                    className="form-control"
                    value={formData.policy.lossHistory.priorClaim3years}
                    onChange={(e) => handlePolicyChange('lossHistory.priorClaim3years', e.target.value)}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                    <option value="-select-">Select</option>
                  </select>
                </div>
              </div>
            </details>

            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', marginBottom: '15px' }}>
                Optional Coverages
              </summary>
              <div style={{ marginLeft: '15px' }}>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={!!formData.policy.optionalCoverages?.miscellaneous?.terrorism}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handlePolicyChange('optionalCoverages.miscellaneous.terrorism', {
                            name: 'TRIA',
                            title: 'Terrorism Risk Insurance Act Coverage'
                          });
                        } else {
                          handlePolicyChange('optionalCoverages.miscellaneous.terrorism', null);
                        }
                      }}
                    />
                    {' '}TRIA Terrorism Coverage
                  </label>
                </div>

                {/* Additional Insured Section */}
                <div className="mt-3">
                  <div className="flex-between mb-2">
                    <h5>Additional Insured Forms</h5>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={handleAddAdditionalInsured}
                    >
                      Add Additional Insured
                    </button>
                  </div>

                  {getAdditionalInsuredList().map((ai) => (
                    <div key={ai.key} className="card" style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f8f9fa' }}>
                      <div className="flex-between mb-2">
                        <strong>Additional Insured #{ai.key}</strong>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => handleRemoveAdditionalInsured(ai.key)}
                        >
                          Remove
                        </button>
                      </div>

                      <div className="grid">
                        <div className="form-group">
                          <label>Form *</label>
                          <SearchableSelect
                            options={AI_FORMS}
                            value={ai.form}
                            onChange={(value) => handleAdditionalInsuredChange(ai.key, 'form', value)}
                            placeholder="Select AI form..."
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>
                            <input
                              type="checkbox"
                              checked={ai.waiver || false}
                              onChange={(e) => handleAdditionalInsuredChange(ai.key, 'waiver', e.target.checked)}
                            />
                            {' '}Include Waiver of Subrogation (+$50)
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}

                  {getAdditionalInsuredList().length === 0 && (
                    <div className="text-muted" style={{ padding: '10px', textAlign: 'center' }}>
                      No additional insured forms added. Click "Add Additional Insured" to add one.
                    </div>
                  )}
                </div>

                {/* GL Miscellaneous Optional Coverages */}
                <div className="mt-3">
                  <h5>Additional Optional Coverages</h5>

                  <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={!!formData.policy.lineGL.optionalCoverages?.Miscellaneous?.lostKeyCoverage}
                          onChange={(e) => handleGLMiscellaneousChange('lostKeyCoverage', e.target.checked)}
                        />
                        {' '}Lost Key Coverage (+$150)
                      </label>
                    </div>

                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={!!formData.policy.lineGL.optionalCoverages?.Miscellaneous?.voluntaryDamageToProperty}
                          onChange={(e) => handleGLMiscellaneousChange('voluntaryDamageToProperty', e.target.checked)}
                        />
                        {' '}Voluntary Damage to Property (+$150)
                      </label>
                    </div>

                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={!!formData.policy.lineGL.optionalCoverages?.Miscellaneous?.overspray}
                          onChange={(e) => handleGLMiscellaneousChange('overspray', e.target.checked)}
                        />
                        {' '}Overspray Coverage (+$100)
                      </label>
                    </div>

                  </div>
                </div>
              </div>
            </details>

            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', marginBottom: '15px' }}>
                Special Credits/Debits (Optional)
              </summary>
              <div style={{ marginLeft: '15px' }}>

                <div className="grid">
                  <div className="form-group">
                    <label>Management Credit/Debit</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.policy.specialCrDr?.management || '0'}
                      onChange={(e) => handleSpecialCrDrChange('management', e.target.value)}
                      placeholder="0"
                      min="-7.5"
                      max="7.5"
                      step="0.5"
                    />
                    <small className="form-text text-muted">Range: -7.5 to +7.5, step 0.5</small>
                  </div>

                  <div className="form-group">
                    <label>Safety Programs Credit/Debit</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.policy.specialCrDr?.safetyPrograms || '0'}
                      onChange={(e) => handleSpecialCrDrChange('safetyPrograms', e.target.value)}
                      placeholder="0"
                      min="-7.5"
                      max="7.5"
                      step="0.5"
                    />
                    <small className="form-text text-muted">Range: -7.5 to +7.5, step 0.5</small>
                  </div>

                  <div className="form-group">
                    <label>Supervision/Employee Experience</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.policy.specialCrDr?.supervisionEmployeeExperience || '0'}
                      onChange={(e) => handleSpecialCrDrChange('supervisionEmployeeExperience', e.target.value)}
                      placeholder="0"
                      min="-7.5"
                      max="7.5"
                      step="0.5"
                    />
                    <small className="form-text text-muted">Range: -7.5 to +7.5, step 0.5</small>
                  </div>

                  <div className="form-group">
                    <label>Special Characteristics</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.policy.specialCrDr?.specialCharacteristics || '0'}
                      onChange={(e) => handleSpecialCrDrChange('specialCharacteristics', e.target.value)}
                      placeholder="0"
                      min="-7.5"
                      max="7.5"
                      step="0.5"
                    />
                    <small className="form-text text-muted">Range: -7.5 to +7.5, step 0.5</small>
                  </div>

                  <div className="form-group">
                    <label>Total Credit/Debit (%)</label>
                    <div
                      className="form-control"
                      style={{
                        backgroundColor: '#f8f9fa',
                        border: '2px solid #007bff',
                        fontWeight: 'bold',
                        fontSize: '16px',
                        color: calculateSpecialCrDrTotal() >= 0 ? '#28a745' : '#dc3545'
                      }}
                    >
                      {calculateSpecialCrDrTotal() >= 0 ? '+' : ''}{calculateSpecialCrDrTotal().toFixed(1)}%
                    </div>
                    <small className="form-text text-muted">Calculated sum of all Credit/Debit fields</small>
                  </div>

                  <div className="form-group">
                    <label>Large Risk Discount Factor</label>
                    <select
                      className="form-control"
                      value={formData.policy.specialCrDr?.largeRiskDiscountFactor || '-select-'}
                      onChange={(e) => handleSpecialCrDrChange('largeRiskDiscountFactor', e.target.value)}
                    >
                      <option value="-select-">Select</option>
                      <option value="1.0">1.0 - No Discount</option>
                      <option value="0.95">0.95 - 5% Discount</option>
                      <option value="0.90">0.90 - 10% Discount</option>
                      <option value="0.85">0.85 - 15% Discount</option>
                    </select>
                  </div>
                </div>
              </div>
            </details>

            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', marginBottom: '15px' }}>
                Inland Marine Coverages (Optional)
              </summary>
              <div style={{ marginLeft: '15px' }}>

                {/* Contractors Equipment Scheduled */}
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={!!formData.policy.lineIM?.coverages?.contractorsEquipmentScheduled}
                      onChange={(e) => handleIMCoverageToggle('contractorsEquipmentScheduled', e.target.checked)}
                    />
                    {' '}Contractors Equipment Scheduled (1.9% rate, $250 minimum)
                  </label>
                </div>

                {formData.policy.lineIM?.coverages?.contractorsEquipmentScheduled && (
                  <div style={{ marginLeft: '20px', marginTop: '10px', marginBottom: '15px' }}>
                    <div className="flex-between mb-2">
                      <h5>Scheduled Equipment Items</h5>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={handleAddScheduledItem}
                      >
                        Add Equipment Item
                      </button>
                    </div>

                    {getScheduledItemsList().map((item) => (
                      <div key={item.key} className="card" style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f8f9fa' }}>
                        <div className="flex-between mb-2">
                          <strong>Equipment #{item.key}</strong>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => handleRemoveScheduledItem(item.key)}
                          >
                            Remove
                          </button>
                        </div>

                        <div className="grid">
                          <div className="form-group">
                            <label>Description *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={item.description || ''}
                              onChange={(e) => handleScheduledItemChange(item.key, 'description', e.target.value)}
                              placeholder="e.g., Excavator, Generator, etc."
                              required
                            />
                          </div>

                          <div className="form-group">
                            <label>Limit *</label>
                            <input
                              type="number"
                              className="form-control"
                              value={item.limit || ''}
                              onChange={(e) => handleScheduledItemChange(item.key, 'limit', e.target.value)}
                              placeholder="10000"
                              min="1000"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    {getScheduledItemsList().length === 0 && (
                      <div className="text-muted" style={{ padding: '10px', textAlign: 'center' }}>
                        No equipment items added. Click "Add Equipment Item" to add one.
                      </div>
                    )}
                  </div>
                )}

                {/* Other IM Coverages */}
                <div className="grid">
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={!!formData.policy.lineIM?.coverages?.contractorsEquipmentMiscTools}
                        onChange={(e) => handleIMCoverageToggle('contractorsEquipmentMiscTools', e.target.checked)}
                      />
                      {' '}Contractors Equipment Misc Tools
                    </label>
                    {formData.policy.lineIM?.coverages?.contractorsEquipmentMiscTools && (
                      <div style={{ marginTop: '10px' }}>
                        <label>Limit</label>
                        <select
                          className="form-control"
                          value={formData.policy.lineIM?.coverages?.contractorsEquipmentMiscTools?.limit || 5000}
                          onChange={(e) => handleIMCoverageChange('contractorsEquipmentMiscTools', 'limit', parseInt(e.target.value))}
                        >
                          <option value={5000}>$5,000 ($350 premium)</option>
                          <option value={1000}>$1,000 ($250 premium)</option>
                        </select>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={!!formData.policy.lineIM?.coverages?.installationFloater}
                        onChange={(e) => handleIMCoverageToggle('installationFloater', e.target.checked)}
                      />
                      {' '}Installation Floater
                    </label>
                    {formData.policy.lineIM?.coverages?.installationFloater && (
                      <div style={{ marginTop: '10px' }}>
                        <label>Limit</label>
                        <select
                          className="form-control"
                          value={formData.policy.lineIM?.coverages?.installationFloater?.limit || 5000}
                          onChange={(e) => handleIMCoverageChange('installationFloater', 'limit', parseInt(e.target.value))}
                        >
                          <option value={5000}>$5,000 ($500 premium)</option>
                          <option value={1000}>$1,000 ($350 premium)</option>
                        </select>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={!!formData.policy.lineIM?.coverages?.contractorsInstallationFloater}
                        onChange={(e) => handleIMCoverageToggle('contractorsInstallationFloater', e.target.checked)}
                      />
                      {' '}Contractors Installation Floater (Rate-based)
                    </label>
                    {formData.policy.lineIM?.coverages?.contractorsInstallationFloater && (
                      <div style={{ marginTop: '10px' }}>
                        <label>Limit</label>
                        <select
                          className="form-control"
                          value={formData.policy.lineIM?.coverages?.contractorsInstallationFloater?.limit || 5000}
                          onChange={(e) => handleIMCoverageChange('contractorsInstallationFloater', 'limit', parseInt(e.target.value))}
                        >
                          <option value={5000}>$5,000 (5% rate + $500 minimum)</option>
                          <option value={1000}>$1,000 (7% rate + $350 minimum)</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </details>

          </div>
        </details>

        <div className="mb-3">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || getLocationsList().length === 0 || getRisksList().length === 0}
          >
            {loading ? 'Calculating...' : 'Calculate Premium'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginLeft: '10px' }}
            disabled={loading || getLocationsList().length === 0 || getRisksList().length === 0}
            onClick={handleCalculateAndDownload}
          >
            {loading ? 'Calculating...' : 'Calculate & Download'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ marginLeft: '10px' }}
            onClick={() => {
              setVersionNumber('1.0.0');
              setFormData({
                account: {
                  location: {
                    id1138698: {
                      county: 'DUVAL',
                      state: 'FL',
                      extraData: {
                        idOriginal: 1138698
                      }
                    }
                  }
                },
                policy: {
                  state: 'FL',
                  rateType: 'NB',
                  effectiveDate: formatDate(today),
                  expirationDate: formatDate(nextYear),
                  company: 'Evolve',
                  companyShort: 'Evolve',
                  lineGL: {
                    risk: {
                      '91341_1': {
                        code: 91341,
                        exposure: 150000,
                        extraData: {
                          locationId: 1138698
                        }
                      }
                    },
                    policyPerOccurrenceLimit: 1000000,
                    policyAggregateLimit: 2000000,
                    productCompletedOperationLimit: 2000000,
                    personalAdvertisingInjuryLimit: 1000000,
                    deductibles: {
                      pd: 500.00
                    },
                    optionalCoverages: {
                      additionalInsured: {}
                    }
                  },
                  lossHistory: {
                    items: {},
                    priorClaim3years: 'no'
                  },
                  previousInsuranceItems: {},
                  optionalCoverages: {
                    miscellaneous: {
                      terrorism: {
                        name: 'TRIA',
                        title: 'Terrorism Risk Insurance Act Coverage'
                      }
                    }
                  },
                  specialCrDr: {
                    largeRiskDiscountFactor: '-select-',
                    management: '0',
                    safetyPrograms: '0',
                    supervisionEmployeeExperience: '0',
                    specialCharacteristics: '0'
                  }
                }
              });
              setResult(null);
              setError(null);
              setLastRequest(null);
            }}
          >
            Reset Form
          </button>
        </div>
      </form>

      {error && (
        <div className="error">
          <strong>Error:</strong> {error}
        </div>
      )}


      {result && (
        <details open={sectionsExpanded.calculationResults}>
          <summary
            style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '18px', marginBottom: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}
            onClick={(e) => {
              e.preventDefault();
              toggleSection('calculationResults');
            }}
          >
            Calculation Results
          </summary>
          <div className="results" style={{ marginTop: '10px' }}>
            {result._status === 'ok' ? (
              <div>
                {/* Summary Section */}
                <div className="summary-box">
                  <h4>Policy Summary</h4>
                  <div className="grid">
                    <div>
                      <p><strong>Total Premium:</strong> ${parseFloat(result.policy?.lineGL?._rated?.premium || 0).toFixed(2)}</p>
                      <p><strong>Premium Minimum:</strong> ${parseFloat(result.policy?.lineGL?._rated?.premiumMinimum || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <p><strong>Policy State:</strong> {result.policy?.state || 'N/A'}</p>
                      <p><strong>Company:</strong> {result.policy?.company || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Coverage Limits */}
                <div className="card">
                  <h4>Coverage Limits</h4>
                  <div className="grid">
                    <div>
                      <p><strong>Per Occurrence:</strong> ${result.policy?.lineGL?.policyPerOccurrenceLimit?.toLocaleString() || result.policy?.lineGL?.PolicyPerOccurenceLimit?.toLocaleString() || 'N/A'}</p>
                      <p><strong>Aggregate:</strong> ${result.policy?.lineGL?.policyAggregateLimit?.toLocaleString() || result.policy?.lineGL?.PolicyAggregateLimit?.toLocaleString() || 'N/A'}</p>
                    </div>
                    <div>
                      <p><strong>Products Completed Ops:</strong> ${result.policy?.lineGL?.productCompletedOperationLimit?.toLocaleString() || result.policy?.lineGL?.ProductCompletedOperationLimit?.toLocaleString() || 'N/A'}</p>
                      <p><strong>Personal & Advertising:</strong> ${result.policy?.lineGL?.personalAdvertisingInjuryLimit?.toLocaleString() || result.policy?.lineGL?.PersonalAdvertisingInjuryLimit?.toLocaleString() || 'N/A'}</p>
                    </div>
                  </div>
                  {result.policy?.lineGL?.deductibles && (
                    <p><strong>Property Damage Deductible:</strong> ${result.policy.lineGL.deductibles.pd?.toLocaleString() || result.policy.lineGL.Deductibles?.PD?.toLocaleString() || '0'}</p>
                  )}
                </div>

                {/* Risk Details by Location */}
                {result.policy?.lineGL?.risk && (
                  <div className="card">
                    <h4>Risk Details by Location & Class Code</h4>
                    {Object.keys(result.policy.lineGL.risk).map(riskKey => {
                      const risk = result.policy.lineGL.risk[riskKey];
                      const locationId = risk.extraData?.locationId;
                      const location = result.account?.location?.[`id${locationId}`];

                      return (
                        <div key={riskKey} className="card" style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#f8f9fa' }}>
                          <h5>Risk: {riskKey}</h5>

                          {/* Location Info */}
                          {location && (
                            <div style={{ marginBottom: '10px' }}>
                              <strong>Location:</strong> {location.county}, {location.state} (ID: {locationId})
                            </div>
                          )}

                          {/* Risk Basic Info */}
                          <div className="grid">
                            <div>
                              <p><strong>Class Code:</strong> {risk.code}</p>
                              <p><strong>Exposure (Payroll):</strong> ${risk.exposure?.toLocaleString()}</p>
                            </div>
                            <div>
                              {risk._rated && (
                                <>
                                  <p><strong>Premium:</strong> ${parseFloat(risk._rated.premium || 0).toFixed(2)}</p>
                                  <p><strong>Rate:</strong> {parseFloat(risk._rated.rates?.final || risk._rated.rates?.rate || 0).toFixed(4)}</p>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Risk Rating Details */}
                          {risk._rated?.rates && (
                            <div style={{ marginTop: '10px' }}>
                              <JsonViewer
                                data={risk._rated.rates}
                                title="Rating Details"
                                defaultCollapsed={true}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Optional Coverages */}
                {(result.policy?.optionalCoverages || result.policy?.lineGL?.optionalCoverages) && (
                  <div className="card">
                    <h4>Optional Coverages</h4>

                    {/* Terrorism Coverage */}
                    {result.policy?.optionalCoverages?.miscellaneous?.terrorism?._rated && (
                      <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                        <h5>TRIA Terrorism Coverage</h5>
                        <p><strong>Premium:</strong> ${parseFloat(result.policy.optionalCoverages.miscellaneous.terrorism._rated.premium || 0).toFixed(2)}</p>
                        <p><strong>Rate:</strong> {safeDisplay(result.policy.optionalCoverages.miscellaneous.terrorism._rated.rates?.tria)}</p>
                      </div>
                    )}

                    {/* Additional Insured */}
                    {(result.policy?.lineGL?.optionalCoverages?.AdditionalInsured || result.policy?.lineGL?.optionalCoverages?.additionalInsured) && (
                      <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                        <h5>Additional Insured</h5>
                        {(() => {
                          const ais = result.policy.lineGL.optionalCoverages.AdditionalInsured || result.policy.lineGL.optionalCoverages.additionalInsured || {};
                          return Object.keys(ais).map(aiKey => {
                            const ai = ais[aiKey];
                            return (
                              <div key={aiKey} style={{ marginBottom: '10px', padding: '8px', backgroundColor: '#fff', borderRadius: '3px', border: '1px solid #dee2e6' }}>
                                <p><strong>Form {aiKey}: {ai.form}</strong></p>
                                {ai._rated && (
                                  <div style={{ marginLeft: '10px' }}>
                                    <p><strong>Premium:</strong> ${parseFloat(ai._rated.premium || 0).toFixed(2)}</p>
                                    {ai._rated.rates && (
                                      <p><strong>Rate:</strong> {safeDisplay(ai._rated.rates.rate || ai._rated.rates)}</p>
                                    )}
                                    {ai.waiver && ai._rated.premiumWaiver && (
                                      <p><strong>Waiver Premium:</strong> ${parseFloat(ai._rated.premiumWaiver || 0).toFixed(2)}</p>
                                    )}
                                    {ai._rated.included && <p><em>Included at no charge</em></p>}
                                    {ai._rated.tbd && <p><em>To be determined</em></p>}
                                  </div>
                                )}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}

                    {/* GL Miscellaneous Optional Coverages */}
                    {result.policy?.lineGL?.optionalCoverages?.Miscellaneous && Object.keys(result.policy.lineGL.optionalCoverages.Miscellaneous).length > 0 && (
                      <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                        <h5>GL Miscellaneous Optional Coverages</h5>
                        {Object.keys(result.policy.lineGL.optionalCoverages.Miscellaneous).map(coverageKey => {
                          const coverage = result.policy.lineGL.optionalCoverages.Miscellaneous[coverageKey];
                          const coverageNames = {
                            lostKeyCoverage: 'Lost Key Coverage',
                            voluntaryDamageToProperty: 'Voluntary Damage to Property',
                            overspray: 'Overspray Coverage'
                          };

                          return (
                            <div key={coverageKey} style={{ marginBottom: '8px', padding: '6px', backgroundColor: '#fff', borderRadius: '3px', border: '1px solid #dee2e6' }}>
                              <p><strong>{coverageNames[coverageKey] || coverageKey}</strong></p>
                              {coverage._rated && (
                                <div style={{ marginLeft: '10px' }}>
                                  <p><strong>Premium:</strong> ${parseFloat(coverage._rated.premium || 0).toFixed(2)}</p>
                                  {coverage._rated.rates && (
                                    <p><strong>Rate:</strong> {safeDisplay(coverage._rated.rates.value || coverage._rated.rates)}</p>
                                  )}
                                  {coverage._rated.included && <p><em>Included at no charge</em></p>}
                                  {coverage._rated.tbd && <p><em>To be determined</em></p>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Special Credits/Debits */}
                {result.policy?.specialCrDr?._rated && (
                  <div className="card">
                    <h4>Special Credits/Debits Applied</h4>
                    <div className="grid">
                      {result.policy.specialCrDr._rated.rates?.management && (
                        <p><strong>Management:</strong> {safeDisplay(result.policy.specialCrDr._rated.rates.management)}</p>
                      )}
                      {result.policy.specialCrDr._rated.rates?.safetyPrograms && (
                        <p><strong>Safety Programs:</strong> {safeDisplay(result.policy.specialCrDr._rated.rates.safetyPrograms)}</p>
                      )}
                      {result.policy.specialCrDr._rated.rates?.supervisionEmployeeExperience && (
                        <p><strong>Supervision/Experience:</strong> {safeDisplay(result.policy.specialCrDr._rated.rates.supervisionEmployeeExperience)}</p>
                      )}
                      {result.policy.specialCrDr._rated.rates?.specialCharacteristics && (
                        <p><strong>Special Characteristics:</strong> {safeDisplay(result.policy.specialCrDr._rated.rates.specialCharacteristics)}</p>
                      )}
                      {result.policy.specialCrDr._rated.rates?.largeRiskDiscountFactor && (
                        <p><strong>Large Risk Discount:</strong> {safeDisplay(result.policy.specialCrDr._rated.rates.largeRiskDiscountFactor)}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Deductibles Rating */}
                {(result.policy?.lineGL?.deductibles?._rated || result.policy?.lineGL?.Deductibles?._rated) && (
                  <div className="card">
                    <h4>Deductibles Rating</h4>
                    {(result.policy.lineGL.deductibles?._rated || result.policy.lineGL.Deductibles?._rated) && (
                      <div>
                        <p><strong>Deductible Factor:</strong> {safeDisplay(result.policy.lineGL.deductibles?._rated?._rates?.total || result.policy.lineGL.Deductibles?._rated?._rates?.total)}</p>
                        <p><strong>PD Factor:</strong> {safeDisplay(result.policy.lineGL.deductibles?._rated?._rates?.PD || result.policy.lineGL.Deductibles?._rated?._rates?.PD)}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Company Modifiers */}
                {result.policy?.lineGL?._rated?.rates && (
                  <div className="card">
                    <h4>Company Modifiers</h4>
                    <div className="grid">
                      {result.policy.lineGL._rated.rates.companyMod && (
                        <p><strong>Company Modifier:</strong> {safeDisplay(result.policy.lineGL._rated.rates.companyMod)}</p>
                      )}
                      {result.policy.lineGL._rated.premiumMinimum && (
                        <p><strong>Premium Minimum:</strong> ${parseFloat(result.policy.lineGL._rated.premiumMinimum || 0).toFixed(2)}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Fees & Inspection */}
                {result.policy?._rated && (
                  <div className="card">
                    <h4>Fees & Inspection</h4>
                    <div className="grid">
                      {result.policy._rated.policyfee?.value !== undefined && (
                        <p><strong>Policy Fee:</strong> ${parseFloat(result.policy._rated.policyfee.value || 0).toFixed(2)}</p>
                      )}
                      {result.policy._rated.inspection !== undefined && (
                        <p><strong>Inspection Fee:</strong> ${parseFloat(result.policy._rated.inspection || 0).toFixed(2)}</p>
                      )}
                      {result.policy._rated.fee !== undefined && (
                        <p><strong>Total Fees:</strong> ${parseFloat(result.policy._rated.fee || 0).toFixed(2)}</p>
                      )}
                    </div>

                    {/* Inspection by Location */}
                    {result.account?.location && Object.keys(result.account.location).some(locKey =>
                      result.account.location[locKey]._rated?.inspection > 0
                    ) && (
                      <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                        <h5>Inspection Fee by Location</h5>
                        {Object.keys(result.account.location).map(locationKey => {
                          const location = result.account.location[locationKey];
                          if (location._rated?.inspection > 0) {
                            return (
                              <p key={locationKey}>
                                <strong>{location.county}, {location.state}:</strong> ${parseFloat(location._rated.inspection || 0).toFixed(2)}
                              </p>
                            );
                          }
                          return null;
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Inland Marine Results */}
                {result.policy?.lineIM?._rated && (
                  <div className="card">
                    <h4>Inland Marine Coverage Results</h4>

                    {/* Scheduled Equipment */}
                    {result.policy.lineIM.coverages?.contractorsEquipmentScheduled?._rated && (
                      <div style={{ marginBottom: '15px' }}>
                        <h5>Contractors Equipment Scheduled</h5>
                        <p><strong>Total Premium:</strong> ${parseFloat(result.policy.lineIM.coverages.contractorsEquipmentScheduled._rated.premium || 0).toFixed(2)}</p>

                        {result.policy.lineIM.coverages.contractorsEquipmentScheduled.items && Object.keys(result.policy.lineIM.coverages.contractorsEquipmentScheduled.items).map(itemKey => {
                          const item = result.policy.lineIM.coverages.contractorsEquipmentScheduled.items[itemKey];
                          return (
                            <div key={itemKey} style={{ marginLeft: '15px', marginBottom: '8px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '3px' }}>
                              <p><strong>{item.description || itemKey}:</strong> Limit ${item.limit?.toLocaleString() || '0'}</p>
                              {item._rated && <p>Premium: ${parseFloat(item._rated.premium || 0).toFixed(2)}</p>}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Misc Tools */}
                    {result.policy.lineIM.coverages?.contractorsEquipmentMiscTools?._rated && (
                      <div style={{ marginBottom: '10px' }}>
                        <p><strong>Contractors Equipment Misc Tools:</strong> ${parseFloat(result.policy.lineIM.coverages.contractorsEquipmentMiscTools._rated.premium || 0).toFixed(2)}</p>
                      </div>
                    )}

                    {/* Installation Floater */}
                    {result.policy.lineIM.coverages?.installationFloater?._rated && (
                      <div style={{ marginBottom: '10px' }}>
                        <p><strong>Installation Floater:</strong> ${parseFloat(result.policy.lineIM.coverages.installationFloater._rated.premium || 0).toFixed(2)}</p>
                      </div>
                    )}

                    {/* Contractors Installation Floater */}
                    {result.policy.lineIM.coverages?.contractorsInstallationFloater?._rated && (
                      <div style={{ marginBottom: '10px' }}>
                        <p><strong>Contractors Installation Floater:</strong> ${parseFloat(result.policy.lineIM.coverages.contractorsInstallationFloater._rated.premium || 0).toFixed(2)}</p>
                      </div>
                    )}

                    {/* Total IM Premium */}
                    <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e7f3ff', borderRadius: '4px' }}>
                      <p><strong>Total Inland Marine Premium:</strong> ${(() => {
                        let total = 0;
                        if (result.policy.lineIM.coverages) {
                          Object.keys(result.policy.lineIM.coverages).forEach(coverageKey => {
                            const coverage = result.policy.lineIM.coverages[coverageKey];
                            if (coverage._rated && coverage._rated.premium) {
                              total += parseFloat(coverage._rated.premium);
                            }
                          });
                        }
                        return total.toFixed(2);
                      })()}</p>
                    </div>
                  </div>
                )}

              </div>
            ) : (
              <div className="error">
                <strong>Calculation Failed</strong>
                <p>Check the Response JSON below for error details.</p>
              </div>
            )}
          </div>
        </details>
      )}

      {(lastRequest || result) && (
        <details open={sectionsExpanded.requestResponse}>
          <summary
            style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '18px', marginBottom: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}
            onClick={(e) => {
              e.preventDefault();
              toggleSection('requestResponse');
            }}
          >
            Request & Response Data
          </summary>
          <div className="results" style={{ marginTop: '10px' }}>

            {lastRequest && (
              <JsonViewer
                data={lastRequest}
                title="Request JSON"
                defaultCollapsed={true}
              />
            )}

            {result && (
              <JsonViewer
                data={result}
                title="Response JSON"
                defaultCollapsed={true}
              />
            )}
          </div>
        </details>
      )}

      <ApiDocumentation
        isOpen={showDocumentation}
        onClose={() => setShowDocumentation(false)}
      />
    </div>
  );
};

export default ArtisanEvolve;